import * as React from "react";
import Switch from "@mui/material/Switch";

interface CustomProps {
  checked: boolean;
  onChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => void;
  name: string;
  disabled?: boolean;
  /**
   * Accessible name for the underlying checkbox input. Supply this whenever the
   * switch has no associated visible `<label>` so assistive tech can announce it.
   */
  "aria-label"?: string;
  /**
   * Id of a visible label element that names the switch. Prefer this over
   * `aria-label` when the label text is already on screen (WCAG 1.3.1 / 4.1.2).
   */
  "aria-labelledby"?: string;
}

/**
 * Controlled toggle used across the app.
 *
 * Carries no styling of its own — the look lives in the global `MuiSwitch`
 * override (`theme/light/create-components.ts`, values from `switchControl` in
 * `theme/colors.ts`), so this and a bare `<Switch>` render identically. It
 * stays only to keep `name` / `checked` / `onChange` required at the call site.
 */
const CustomSwitch = (props: CustomProps) => {
  const inputProps: React.InputHTMLAttributes<HTMLInputElement> = {};
  if (props["aria-label"]) inputProps["aria-label"] = props["aria-label"];
  if (props["aria-labelledby"])
    inputProps["aria-labelledby"] = props["aria-labelledby"];

  return (
    <Switch
      name={props.name}
      disabled={props.disabled}
      checked={props.checked}
      onChange={props.onChange}
      inputProps={Object.keys(inputProps).length ? inputProps : undefined}
    />
  );
};

export default CustomSwitch;
